import { KeyboardEvent } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { classNames } from "helpers/commonHelpers";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save/RequestFormSaveContext";
import { useReviewFieldErrorClear } from "@RequestFormV2/hooks/useReviewFieldErrorClear";
import { ReviewFieldErrorClearWatcher } from "@RequestFormV2/hooks/ReviewFieldErrorClearWatcher";
import { stepSpecialRequirementNumeric } from "helpers/validationHelpers";
import "./NumericInput.scss";

function handleIntegerKeyPress(event: KeyboardEvent<HTMLInputElement>) {
  const { key, currentTarget } = event;
  if (key.length !== 1) return;

  if (/\d/.test(key)) return;

  if (key === "-" && currentTarget.selectionStart === 0 && !currentTarget.value.includes("-")) return;

  event.preventDefault();
}

function handleDoubleKeyPress(event: KeyboardEvent<HTMLInputElement>) {
  const { key, currentTarget } = event;
  if (key.length !== 1) return;

  if (/\d/.test(key)) return;

  if (key === "-" && currentTarget.selectionStart === 0 && !currentTarget.value.includes("-")) return;

  if (key === "." && !currentTarget.value.replace("-", "").includes(".")) return;

  event.preventDefault();
}

interface NumericInputProps {
  name: string;
  dataType: "integer" | "double";
  required?: boolean;
  placeholder?: string;
  registerOptions?: object;
  shouldUnregister?: boolean;
  testId?: string;
  width?: string | number;
  label?: string;
  hintText?: string;
  disabled?: boolean;
  saveOnBlur?: boolean;
  onAfterBlur?: () => void;
  validateOnChange?: boolean;
  disableReviewErrorClear?: boolean;
}

const NumericInput = function ({
  name,
  dataType,
  required = false,
  placeholder = "",
  registerOptions = {},
  shouldUnregister = false,
  testId,
  width = "100%",
  label,
  hintText,
  disabled = false,
  saveOnBlur = true,
  onAfterBlur,
  validateOnChange = false,
  disableReviewErrorClear = false,
}: NumericInputProps) {
  const resolvedTestId =
    testId || (name ? `form-field-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}` : undefined);
  const formContext = useFormContext();
  const control = formContext ? formContext.control : null;
  const trigger = formContext?.trigger;
  const saveContext = useRequestFormSaveOrNull();
  const clearReviewError = useReviewFieldErrorClear(disableReviewErrorClear ? undefined : name);
  const onKeyPress = dataType === "integer" ? handleIntegerKeyPress : handleDoubleKeyPress;
  const setValueAsFn =
    typeof (registerOptions as { setValueAs?: (value: string) => string }).setValueAs === "function"
      ? (registerOptions as { setValueAs: (value: string) => string }).setValueAs
      : undefined;

  const normalizeValue = (raw: string) =>
    setValueAsFn ? setValueAsFn(raw) : raw;

  if (!name || !control) {
    return null;
  }

  return (
    <>
      {!disableReviewErrorClear && <ReviewFieldErrorClearWatcher fieldPath={name} />}
      <Controller
        name={name}
        control={control}
        shouldUnregister={shouldUnregister}
        rules={{ required, ...registerOptions }}
        render={({ field, fieldState }) => {
          const { error } = fieldState;
          const value = field.value ?? "";

          const handleStep = (direction: 1 | -1) => {
            field.onChange(stepSpecialRequirementNumeric(value, direction, dataType));
            if (validateOnChange && trigger) {
              trigger(name).catch(() => undefined);
            }
          };

          return (
            <div className="numericInputV2" style={{ width }}>
            {label && (
              <label className={classNames({ required })}>{label}</label>
            )}
            <div className={`input-container ${error ? "error" : ""} ${disabled ? "disabled" : ""}`}>
              <input
                type="text"
                inputMode="decimal"
                style={{ width: "100%" }}
                data-testid={resolvedTestId}
                disabled={disabled}
                value={value}
                placeholder={placeholder}
                onChange={(e) => {
                  const normalized = normalizeValue(e.target.value);
                  field.onChange(normalized);
                  if (!disableReviewErrorClear) clearReviewError(normalized);
                  if (validateOnChange && trigger) {
                    trigger(name).catch(() => undefined);
                  }
                }}
                onBlur={(e) => {
                  const normalized = normalizeValue(e.target.value);
                  if (normalized !== e.target.value) {
                    field.onChange(normalized);
                  }
                  field.onBlur();
                  if (saveOnBlur && saveContext) {
                    saveContext.saveField(name, normalized).catch(() => undefined);
                  }
                  onAfterBlur?.();
                }}
                onKeyPress={onKeyPress}
              />
              <div className="numericInputV2__stepper">
                <button
                  type="button"
                  className="numericInputV2__stepper-button"
                  aria-label="Decrease value"
                  disabled={disabled}
                  onClick={() => handleStep(-1)}
                >
                  -
                </button>
                <button
                  type="button"
                  className="numericInputV2__stepper-button"
                  aria-label="Increase value"
                  disabled={disabled}
                  onClick={() => handleStep(1)}
                >
                  +
                </button>
              </div>
            </div>
            {error && (
              <div className="text-sm error-text text-error-primary">
                {error.message ? (
                  error.message
                ) : (
                  <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
                )}
              </div>
            )}
            {hintText && !error && <div className="text-sm hint-text">{hintText}</div>}
          </div>
          );
        }}
      />
    </>
  );
};

export default NumericInput;
